import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";

// Ported verbatim from the Lovable build.

const rows = [
  { label: "Bank reconciliations", figure: "£1,284,220" },
  { label: "Sales ledger", figure: "£862,940" },
  { label: "Purchase ledger", figure: "£511,375" },
  { label: "Accruals & prepayments", figure: "£97,410" },
  { label: "Intercompany eliminations", figure: "£340,180" },
  { label: "Revenue recognition", figure: "£1,905,660" },
  { label: "Audit pack & schedules", figure: "Prepared" },
];

const STEP_MS = 620;
const HOLD_MS = 4000;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const MonthEndClosePanel = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const reduced = prefersReducedMotion();
  const [done, setDone] = useState(reduced ? rows.length : 0);
  const [flashIndex, setFlashIndex] = useState<number | null>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (reduced) return;
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    const fallback = window.setTimeout(() => setActive(true), 1500);
    return () => {
      observer.disconnect();
      window.clearTimeout(fallback);
    };
  }, [reduced]);

  useEffect(() => {
    if (reduced || !active) return;
    let cancelled = false;
    let timer: number;

    const run = (index: number) => {
      if (cancelled) return;
      if (index < rows.length) {
        timer = window.setTimeout(() => {
          if (cancelled) return;
          setDone(index + 1);
          setFlashIndex(index);
          window.setTimeout(() => setFlashIndex(null), 480);
          run(index + 1);
        }, STEP_MS);
      } else {
        timer = window.setTimeout(() => {
          if (cancelled) return;
          setDone(0);
          run(0);
        }, HOLD_MS);
      }
    };
    run(0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [active, reduced]);

  const pct = Math.round((done / rows.length) * 100);
  const complete = done === rows.length;

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label="Illustrative month-end close panel showing seven reconciliation tasks - bank reconciliations, sales ledger, purchase ledger, accruals and prepayments, intercompany eliminations, revenue recognition and audit pack - reconciling to 100% complete in four working days."
      className="border"
      style={{
        backgroundColor: "rgba(255,255,255,0.035)",
        borderColor: "rgba(255,255,255,0.13)",
      }}
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-4 flex items-start justify-between gap-4">
        <div>
          {/* flex-wrap + nowrap on the title: inside a ~340px mobile panel the
              title otherwise breaks mid-phrase beside the chip; this way the
              chip drops below as a unit instead. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <p className="font-heading text-lg font-semibold whitespace-nowrap text-[hsl(var(--on-ink))]">
              Month-end close
            </p>
            {/* A consultancy showing an animated panel risks reading as a
                product. This label was 11px at 40% opacity in the footer,
                which nobody sees. Then it was 9px, which is smaller than
                anything else on the site and below the point where the
                letterforms of this typeface hold together - a disclaimer
                nobody can read is not a disclaimer. */}
            <span
              className="font-eyebrow rounded px-2 py-1 text-[hsl(var(--on-ink)/0.6)]"
              style={{ border: "1px solid rgba(255,255,255,0.18)", fontSize: 11 }}
            >
              Illustrative
            </span>
          </div>
          <p className="font-eyebrow mt-1 text-[hsl(var(--on-ink)/0.55)]">
            March · Group consolidation
          </p>
        </div>
        <div className="text-right">
          <p className="font-heading text-3xl font-semibold tabular text-[hsl(var(--green-bright))]">
            {pct}%
          </p>
          <p className="font-eyebrow mt-1 text-[hsl(var(--on-ink)/0.55)]">Reconciled</p>
        </div>
      </div>

      <div className="h-[2px] w-full bg-[rgba(255,255,255,0.10)]">
        <div
          className="h-full bg-[hsl(var(--green-bright))] transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Rows */}
      <div>
        {rows.map((row, i) => {
          const isDone = i < done;
          return (
            <div
              key={row.label}
              className="relative flex items-center gap-3 px-5 py-3 border-b transition-colors duration-500"
              style={{
                borderColor: "rgba(255,255,255,0.08)",
                backgroundColor:
                  flashIndex === i ? "rgba(79,190,148,0.10)" : "transparent",
              }}
            >
              <span
                className="shrink-0 flex items-center justify-center border transition-colors duration-300"
                style={{
                  width: 17,
                  height: 17,
                  backgroundColor: isDone
                    ? "hsl(var(--green-bright))"
                    : "transparent",
                  borderColor: isDone
                    ? "hsl(var(--green-bright))"
                    : "hsl(var(--amber-bright))",
                }}
              >
                {isDone && <Check size={12} strokeWidth={3} className="text-[hsl(var(--ink))]" />}
              </span>
              <span className="text-sm flex-1 text-[hsl(var(--on-ink)/0.85)]">{row.label}</span>
              <span
                className="font-mono text-xs tabular transition-colors duration-300"
                style={{
                  color: isDone
                    ? "hsl(var(--on-ink) / 0.5)"
                    : "hsl(var(--amber-bright))",
                }}
              >
                {row.figure}
              </span>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-5 py-3 flex items-center justify-end gap-4">
        <span className="font-eyebrow text-[hsl(var(--on-ink)/0.55)]">
          {complete ? "Closed · 4 working days" : "Closing…"}
        </span>
      </div>
    </div>
  );
};

export default MonthEndClosePanel;
